import { Image, Text, View } from "@react-pdf/renderer";

import { formatResourceUrl } from "utils/common";

import { usePdfChromeScale } from "../pdf-page-chrome";

type SignatureSlotProps = {
  signedAtDate?: string;
  signatureImgUrl?: string;
  signatureSize?: { width: number; height: number };
  signatureLabel?: string;
  providerFullName?: string;
  licenseNumber?: string;
  signaturesCount?: number;
  compact?: boolean;
  labelWidth?: string;
};

export default function SignatureSlot({
  signedAtDate,
  signatureImgUrl,
  signatureLabel,
  providerFullName,
  licenseNumber,
  signaturesCount = 1,
  compact = false,
  labelWidth
}: SignatureSlotProps) {
  // ENG-2277: lengths are a legacy page's px, scaled on a Form's page (see pdf-page-chrome).
  const { px, scale } = usePdfChromeScale();
  const multiline = signaturesCount > 1;
  const showSignature = Boolean(signedAtDate) && Boolean(signatureImgUrl);

  // The footer's purple rule doubles as the signature line: the label sits on it and the
  // name and license hang below it. Both layouts below preserve that relationship — the
  // label is anchored just above the slot's baseline and the name/license at negative
  // offsets, so they clear the rule instead of being struck through by it.
  //
  // Three signatures do not fit the fixed 500px slot, so past two the row divides evenly
  // and the type steps down (SN-19170). One and two signatures keep the original widths.
  if (compact) {
    const textWidth = "100%";
    return (
      <View
        style={{
          width: `${100 / signaturesCount}%`,
          height: px(80),
          paddingRight: px(10)
        }}
      >
        {showSignature && (
          <View
            style={{
              width: "100%",
              height: px(56),
              display: "flex",
              justifyContent: "flex-end"
            }}
          >
            <Image
              style={{
                objectFit: "scale-down",
                objectPosition: "left bottom",
                paddingBottom: px(2),
                width: "100%",
                height: "auto"
              }}
              src={formatResourceUrl(signatureImgUrl)}
            />
          </View>
        )}
        <Text
          style={{
            fontFamily: "LatoRegular",
            fontSize: px(9),
            position: "absolute",
            bottom: 8 * scale,
            width: textWidth,
            fontWeight: "bold"
          }}
        >
          {signatureLabel}
        </Text>
        <Text
          style={{
            fontFamily: "LatoRegular",
            fontSize: px(9),
            position: "absolute",
            bottom: -11 * scale,
            width: textWidth
          }}
        >
          {providerFullName}
        </Text>
        {Boolean(licenseNumber) && (
          <Text
            style={{
              fontFamily: "LatoRegular",
              fontSize: px(9),
              position: "absolute",
              bottom: -22 * scale,
              width: textWidth
            }}
          >
            (License# {licenseNumber})
          </Text>
        )}
      </View>
    );
  }

  return (
    <View
      style={{
        width: px(500),
        height: px(80),
        paddingLeft: px(110)
      }}
    >
      {showSignature && (
        <View
          style={{
            width: px(240),
            height: px(80),
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <Image
            style={{
              objectFit: "scale-down",
              paddingLeft: px(15),
              paddingRight: px(5),
              paddingBottom: px(2),
              width: "100%",
              height: "auto"
            }}
            src={formatResourceUrl(signatureImgUrl)}
          />
        </View>
      )}
      <Text
        style={{
          fontFamily: "LatoRegular",
          fontSize: px(12),
          position: "absolute",
          bottom: 8 * scale,
          // Without an explicit width the absolute label shrinks to a narrow column, so a
          // long label ("Supervising Provider Signature (Direct)") wraps onto three lines
          // that climb into the signature image. Opt-in per caller: chart notes and the
          // custom print form keep their existing rendering untouched.
          ...(labelWidth ? { width: labelWidth } : {}),
          fontWeight: "bold"
        }}
      >
        {signatureLabel}
      </Text>
      <Text
        style={{
          fontFamily: "LatoRegular",
          fontSize: px(12),
          position: "absolute",
          bottom: -12 * scale,
          width: px(500)
        }}
      >
        {providerFullName}{" "}
        {!multiline && licenseNumber ? `(License# ${licenseNumber})` : ""}
      </Text>
      {multiline && Boolean(licenseNumber) && (
        <Text
          style={{
            fontFamily: "LatoRegular",
            fontSize: px(12),
            position: "absolute",
            bottom: -24 * scale,
            width: px(400)
          }}
        >
          (License# {licenseNumber})
        </Text>
      )}
    </View>
  );
}
